import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'gleam_auth_token';

// Validate token format before use (basic JWT structure check)
const sanitizeToken = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  // JWT has 3 dot-separated base64 parts
  const parts = raw.split('.');
  if (parts.length !== 3) return null;
  return raw;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => sanitizeToken(localStorage.getItem(TOKEN_KEY)));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (_) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  // Cross-tab logout sync: if another tab clears the token, log out everywhere.
  // Also blocks tampering — if a tab sets a token with an invalid shape, reject it.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== TOKEN_KEY) return;
      const safe = sanitizeToken(e.newValue);
      if (safe !== token) {
        setToken(safe);
        if (!safe) setUser(null);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [token]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem(TOKEN_KEY, access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  }, []);

  const register = useCallback(async (email, password, fullName, phone) => {
    // Pick up any stashed referral code (set by /r/:code landing page)
    const referralCode = (typeof localStorage !== 'undefined' && localStorage.getItem('gg_referral_code')) || null;
    const response = await axios.post(`${API_URL}/api/auth/register`, {
      email,
      password,
      full_name: fullName,
      phone,
      ...(referralCode ? { referral_code: referralCode } : {}),
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem(TOKEN_KEY, access_token);
    // Clear referral code once consumed
    if (referralCode) localStorage.removeItem('gg_referral_code');
    setToken(access_token);
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    if (!token) return null;
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      return response.data;
    } catch (_) {
      return null;
    }
  }, [token]);

  // Server re-issues the token for another authorized view of the same account
  const switchView = useCallback(async (view) => {
    const response = await axios.post(`${API_URL}/api/auth/view`, { view }, { headers: { Authorization: `Bearer ${token}` } });
    const { access_token, user: userData } = response.data;
    localStorage.setItem(TOKEN_KEY, access_token);
    return userData;
  }, [token]);

  const updateUserLocal = useCallback((updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : prev);
  }, []);

  const value = useMemo(() => ({
    user,
    token,
    loading,
    login,
    register,
    logout,
    refreshUser,
    updateUserLocal,
    switchView,
    canSwitchViews: (user?.available_views || []).length > 1,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'master_admin',
    isMasterAdmin: user?.role === 'master_admin',
    isCleaner: user?.role === 'cleaner',
    isClient: user?.role === 'client',
    isExistingClient: user?.is_existing_client
  }), [user, token, loading, login, register, logout, refreshUser, updateUserLocal, switchView]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
